import React,{useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const {
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
  FiTrash2
}=FiIcons;

export default function ConversionLogsTab({logs,onRefresh}) {
  const [expandedId,setExpandedId]=useState(null);
  const [rawPayloads,setRawPayloads]=useState({});
  const [loadingId,setLoadingId]=useState(null);
  const [deletingId,setDeletingId]=useState(null);
  const [isClearing,setIsClearing]=useState(false);

  const toggleLog=async (log)=> {
    if (expandedId===log.id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(log.id);

    if (rawPayloads[log.id]!==undefined) return;

    setLoadingId(log.id);
    let query=supabase
      .from('raw_order_logs_1787306056654')
      .select('*')
      .order('created_at',{ascending: false})
      .limit(1);

    if (log.order_id) {
      query=query.eq('order_id',log.order_id);
    } else if (log.transaction_id) {
      query=query.eq('transaction_id',log.transaction_id);
    }

    const {data}=await query;
    setRawPayloads((current)=> ({
      ...current,
      [log.id]: data?.[0]?.payload || null
    }));
    setLoadingId(null);
  };

  const deleteLog=async (event,id)=> {
    event.stopPropagation();

    if (!window.confirm('Delete this conversion log permanently?')) return;

    setDeletingId(id);
    const {error}=await supabase.from('conversion_logs').delete().eq('id',id);

    if (error) {
      alert(`Failed to delete conversion log: ${error.message}`);
    } else {
      setExpandedId((current)=> current===id ? null : current);
      onRefresh();
    }

    setDeletingId(null);
  };

  const clearLogs=async ()=> {
    if (!window.confirm('Delete every conversion log permanently? This cannot be undone.')) {
      return;
    }

    setIsClearing(true);
    const {error}=await supabase
      .from('conversion_logs')
      .delete()
      .not('id','is',null);

    if (error) {
      alert(`Failed to clear conversion logs: ${error.message}`);
    } else {
      setExpandedId(null);
      setRawPayloads({});
      onRefresh();
    }

    setIsClearing(false);
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Recent Conversion Logs
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Select an entry to inspect its complete conversion and webhook data.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            className="text-teal-600 hover:text-teal-700 bg-teal-50 px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition"
          >
            <SafeIcon icon={FiRefreshCw} />
            Refresh Table
          </button>
          <button
            onClick={clearLogs}
            disabled={isClearing || logs.length===0}
            className="text-red-600 hover:text-red-700 bg-red-50 px-4 py-2 rounded-lg font-medium flex items-center gap-2 text-sm transition disabled:opacity-60"
          >
            <SafeIcon icon={FiTrash2} />
            {isClearing ? 'Deleting...' : 'Delete All'}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-slate-100 text-slate-700 border-b border-slate-200 uppercase text-xs font-bold tracking-wider">
            <tr>
              <th className="p-4">Time</th>
              <th className="p-4">Slot</th>
              <th className="p-4">Tx ID</th>
              <th className="p-4">Order ID</th>
              <th className="p-4">Amount</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Details</th>
            </tr>
          </thead>
          <tbody>
            {logs.length===0 ? (
              <tr>
                <td colSpan="7" className="p-8 text-center text-slate-500">
                  No logs found.
                </td>
              </tr>
            ) : logs.map((log)=> (
              <React.Fragment key={log.id}>
                <tr
                  onClick={()=> toggleLog(log)}
                  className="border-b border-slate-100 hover:bg-slate-50 transition cursor-pointer"
                >
                  <td className="p-4 text-slate-500 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="p-4 font-bold text-slate-700">
                    #{log.slot_number}
                  </td>
                  <td className="p-4 font-mono text-xs text-slate-600">
                    {log.transaction_id}
                  </td>
                  <td className="p-4 font-medium">{log.order_id || '-'}</td>
                  <td className="p-4 text-green-700 font-bold">${log.amount}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      log.postback_status_code===200
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                      {log.postback_status_code}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-3">
                      <span className="inline-flex items-center gap-1 text-teal-600 font-medium">
                        {expandedId===log.id ? 'Hide' : 'View'}
                        <SafeIcon icon={expandedId===log.id ? FiChevronUp : FiChevronDown} />
                      </span>
                      <button
                        onClick={(event)=> deleteLog(event,log.id)}
                        disabled={deletingId===log.id}
                        aria-label="Delete conversion log"
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      >
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId===log.id && (
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <td colSpan="7" className="p-4">
                      {loadingId===log.id ? (
                        <p className="py-6 text-center text-slate-500">
                          Loading raw webhook data...
                        </p>
                      ) : (
                        <pre className="max-h-96 overflow-auto rounded-lg bg-slate-900 p-4 text-xs leading-relaxed text-teal-200">
                          {JSON.stringify({
                            conversion_log: log,
                            raw_webhook_payload: rawPayloads[log.id] || 'No matching raw webhook payload found.'
                          },null,2)}
                        </pre>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}